import { FieldHtmlNameUtil, FormIdUtil } from '@onshape/www-inertia';

type QualifiedEventHandler = (_name: string, _data: unknown) => void;
type QualifiedFieldMap = Record<string, string | number>;
type QualifiedFunctionArgs = string | QualifiedEventHandler | Record<string, unknown>;
type QualifiedFunction = (_name: string, _args: QualifiedFunctionArgs) => void;
type QualifiedExperienceSource = 'search' | undefined;

const getQualifiedExperienceId = (source?: QualifiedExperienceSource): string => {
  const lang = useSiteLang().lang;

  if (source === 'search') {
    return 'experience-1781721258580';
  }

  switch (lang.value) {
    case 'de':
      return 'experience-1763558402976';
    case 'fr':
      return 'experience-1763567161627';
    case 'it':
      return 'experience-1763567250828';
    case 'ja':
      return 'experience-1763567323304';
    case 'es':
      return 'experience-1763567384027';
    default:
      return 'experience-1763487665851';
  }
};

const getQualifiedDebugConfig = (): { isDebugMode: boolean } => {
  const { hasInitParam } = useWwwLocation();
  const isDebugMode = useEnvContext().isDev || hasInitParam('qualified-debug');

  return {
    isDebugMode,
  };
};

const getQualifiedClient = (): QualifiedFunction | null => {
  if (import.meta.server) return null;

  const qualified = (window as { qualified?: QualifiedFunction }).qualified;
  if (!qualified) {
    console.warn('Qualified not available on window');
    return null;
  }

  return qualified;
};

const setQualifiedLoadingState = ({ isLoading }: { isLoading: boolean }): void => {
  document.querySelector('iframe#q-messenger-frame')?.classList.toggle('form-loading', isLoading);
};

const normalizeQualifiedFieldNames = ({
  qualifiedFields,
}: {
  qualifiedFields: QualifiedFieldMap;
}): QualifiedFieldMap => {
  const normalizedFields: QualifiedFieldMap = {};

  for (const [fieldName, fieldValue] of Object.entries(qualifiedFields)) {
    const normalizedFieldName: string = fieldName.toLocaleLowerCase();
    normalizedFields[normalizedFieldName] = fieldValue;
  }

  return normalizedFields;
};

const waitForQualifiedConversationStart = ({
  qualified,
  isDebugMode,
}: {
  qualified: QualifiedFunction;
  isDebugMode: boolean;
}): Promise<void> => {
  console.log('Waiting for qualified conversation to start');
  return new Promise<void>((res) => {
    qualified('handleEvents', (name, data) => {
      if (isDebugMode) {
        console.log('Qualified event', { name, data });
      }

      if (name === 'startingConversation') {
        res();
      }
    });
  });
};

const openQualifiedSalesChat = async ({
  qualifiedFields,
  emitFormFill,
  source,
}: {
  qualifiedFields?: QualifiedFieldMap;
  emitFormFill?: boolean;
  source?: QualifiedExperienceSource;
} = {}): Promise<boolean> => {
  const qualified = getQualifiedClient();
  if (!qualified) return false;

  const { isDebugMode } = getQualifiedDebugConfig();
  const lang = useSiteLang().lang;

  let experienceId = getQualifiedExperienceId(source);

  console.log('qualified info', { experienceId, lang: lang.value });
  const { getInitParam, hasInitParam } = useWwwLocation();
  if (hasInitParam('qualified-experience-id')) {
    experienceId = getInitParam('qualified-experience-id') || experienceId;
    console.log('Overriding qualified experience id from url param', experienceId);
  }

  if (isDebugMode && qualifiedFields) {
    console.log('Qualified form data', qualifiedFields);
  }

  if (!experienceId) {
    return false;
  }

  setQualifiedLoadingState({ isLoading: true });
  const convoStarted = waitForQualifiedConversationStart({ qualified, isDebugMode });

  if (qualifiedFields && Object.keys(qualifiedFields).length > 0) {
    const normalizedQualifiedFields: QualifiedFieldMap = normalizeQualifiedFieldNames({
      qualifiedFields,
    });
    qualified('saveFormData', normalizedQualifiedFields);
  }

  if (emitFormFill) {
    qualified('emitFormFill', 'custom');
  }

  qualified('showFormExperience', experienceId);

  if (isDebugMode) {
    console.log('Waiting for qualified conversation to start');
  }

  await convoStarted;

  if (isDebugMode) {
    console.log('Qualified conversation started');
  }

  setQualifiedLoadingState({ isLoading: false });

  return true;
};

/** When a user fills out a form, send the data to Qualified */
const qualifiedFormFill = async () => {
  if (import.meta.server) return;

  const { isDebugMode } = getQualifiedDebugConfig();
  if (isDebugMode) {
    console.log('Running qualified form fill setup');
  }

  useHekateEventBus().addEventListener('form-successfully-submitted', async (evt) => {
    const { data } = evt;
    const formDefinition = JSON.parse(JSON.stringify(data.formDefinition));

    // if this is the signup form, wait for the success screen to show
    await new Promise<void>((res) => {
      if (formDefinition.id !== FormIdUtil.names.unifiedSignup) {
        setTimeout(res, 100);
      }

      useHekateEventBus().addEventListener('uss-screen-shown', (evt) => {
        if (evt.data.screenName === 'uss-signup-success') {
          res();
        }
        setTimeout(res, 30 * 1000);
      });
    }); // wait for Qualified to load

    const ignoredFields: Array<string> = [FieldHtmlNameUtil.recaptcha];
    const qualifiedFields: QualifiedFieldMap = formDefinition.displayFields.reduce(
      (acc: Record<string, string>, field: { val: string; htmlName: string }) => {
        if (ignoredFields.includes(field.htmlName)) {
          return acc;
        }

        if (field.val) {
          acc[field.htmlName.toLocaleLowerCase()] = field.val;
        }
        return acc;
      },
      {}
    );
    qualifiedFields['hekateformid'] = formDefinition.id;
    qualifiedFields['name'] =
      `${qualifiedFields['firstname'] || ''} ${qualifiedFields['lastname'] || ''}`.trim();

    await openQualifiedSalesChat({
      qualifiedFields,
      emitFormFill: true,
    });
  });
};

export const useQualified = () => {
  return {
    openQualifiedSalesChat,
    qualifiedFormFill,
  };
};
